import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@lib/firebase/config';
import type { SiteSettings } from '@shared/types/siteSettings';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  siteTheme: SiteSettings['theme'] | null;
  siteSettings: SiteSettings | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme is already applied in index.html to prevent FOUC
const storedTheme = localStorage.getItem('theme') as Theme;
const initialTheme = storedTheme || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  
  const [siteTheme, setSiteTheme] = useState<SiteSettings['theme'] | null>(null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  // Load site settings from Firestore
  useEffect(() => {
    // Load cached settings immediately
    const cachedSettings = localStorage.getItem('site-settings-cache');
    if (cachedSettings) {
      try {
        const data = JSON.parse(cachedSettings) as SiteSettings;
        setSiteSettings(data);
        setSiteTheme(data.theme);
      } catch (e) {
        console.error('Error parsing cached site settings:', e);
      }
    }
    
    const unsubscribe = onSnapshot(
      doc(db, 'site_settings', 'site-settings'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as SiteSettings;
          setSiteSettings(data);
          setSiteTheme(data.theme);
          // Cache settings for next load
          localStorage.setItem('site-settings-cache', JSON.stringify(data));
        }
      },
      (error) => {
        console.error('Error loading site settings:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Apply light/dark mode class
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove both classes first
    root.classList.remove('light', 'dark');
    
    // Add the current theme
    root.classList.add(theme);
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Apply theme colors as CSS custom properties based on current mode
  useEffect(() => {
    if (!siteTheme) return;

    const root = window.document.documentElement;
    const { colors, fonts } = siteTheme;
    
    // Migration: Handle old theme structure (colors.primary) vs new structure (colors.light.primary)
    let currentColors;
    
    // Check if using new structure with light/dark modes
    if ('light' in colors && 'dark' in colors) {
      // New structure: Use the appropriate mode
      currentColors = theme === 'dark' ? colors.dark : colors.light;
    } else {
      // Old structure: Use colors directly (migration fallback)
      currentColors = {
        primary: (colors as any).primary || '#ffffff',
        secondary: (colors as any).secondary || '#f8f9fa',
        accent: (colors as any).accent || '#e63946',
        background: (colors as any).background || '#ffffff',
        text: (colors as any).text || '#0a0a0a',
        subtext: (colors as any).subtext || '#6c757d',
        success: (colors as any).success || '#10b981',
        error: (colors as any).error || '#ef4444',
        warning: (colors as any).warning || '#f59e0b',
        info: (colors as any).info || '#3b82f6',
      };
    }

    // Apply primary colors
    root.style.setProperty('--site-primary', currentColors.primary);
    root.style.setProperty('--site-secondary', currentColors.secondary);
    root.style.setProperty('--site-accent', currentColors.accent);
    root.style.setProperty('--site-background', currentColors.background);
    root.style.setProperty('--site-text', currentColors.text);

    // Apply subtext color
    root.style.setProperty('--color-subtext', currentColors.subtext);

    // Apply semantic colors
    root.style.setProperty('--color-success', currentColors.success);
    root.style.setProperty('--color-error', currentColors.error);
    root.style.setProperty('--color-warning', currentColors.warning);
    root.style.setProperty('--color-info', currentColors.info);

    // Apply fonts
    root.style.setProperty('--font-heading', fonts.heading);
    root.style.setProperty('--font-body', fonts.body);
    
    // Cache colors in localStorage to prevent FOUC on reload
    localStorage.setItem('theme-colors', JSON.stringify(currentColors));
  }, [siteTheme, theme]);

  const toggleTheme = (): void => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, siteTheme, siteSettings }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

